import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { Easing } from "../../../transforms/transform_base.js";
import { Vector2 } from "../../../common/vector2.js";
import { UIRoundedGlow } from "../../common_ui_elements/ui_rounded_glow.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UICanvasRect } from "../../common_ui_elements/ui_canvas_rect.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { OverlayManager } from "../overlay_manager.js";
import type { DailyTaskRewardDto } from "../../../api/dto/daily_tasks.dto.js";
import { dailyTaskRewardIcon } from "../../common_ui_elements/reward_icon.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { formatShortDuration } from "../../../common/common_funcs.js";
import { isTimedRewardType, rewardAmountText } from "../../../common/reward_format.js";
import { RewardWidgetsService } from "../../../reward_widgets/reward_widgets_service.js";
import { ApiError } from "../../../api/http_client.js";
import { OverlayAppear } from "../overlay_appear.js";
import { RewardFlyToHud } from "../../reward_fly/reward_fly_to_hud.js";
import type { RewardFlySource } from "../../reward_fly/reward_fly_to_hud.js";
import { RewardFlySources } from "../../reward_fly/reward_fly_sources.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";

export class DailyCardRewardOverlay extends AbstractOverlay
{
  private static readonly FLIP_HALF_MS = 160;

  private static readonly GLOW_COLOR = "#FF2BE0";
  private static readonly GLOW_FADE_MS = 100;
  private static readonly DIM_COLOR = "rgba(0, 0, 0, 0.55)";
  private static readonly CARD_CORNER_RADIUS = 36;
  private static readonly BUTTON_FADE_MS = 200;

  private static readonly REWARD_ICON_SIZE = 110;
  private static readonly REWARD_COIN_W = 150;
  private static readonly REWARD_COIN_H = 120;
  private static readonly REWARD_AMOUNT_H = 50;
  private static readonly REWARD_AMOUNT_OVERLAP = 30;
  private static readonly REWARD_ITEM_GAP = 15;

  private static readonly APPEAR_DELAY_MS = 20;
  private static readonly INFO_IMG_FROM_SCALE = 0.5;
  private static readonly INFO_IMG_OVERSHOOT = 1.06;
  private static readonly CLOSE_FROM_SCALE = 0.6;

  private readonly cards: UIImage[] = [];
  private readonly card_bounds = new Map<UIImage, { x: number; y: number; w: number; h: number }>();
  private readonly card_reward_icons = new Map<UIImage, { reward: DailyTaskRewardDto; icon: UIImage }[]>();

  private selected_card: UIImage | null = null;
  private selected_glow: UIRoundedGlow | null = null;
  private bottom_text: UITextElement | null = null;
  private claim_button: UIImage | null = null;
  private card_rewards: DailyTaskRewardDto[][] = [];
  private is_claiming = false;
  private timer_text: UITextElement | null = null;
  private timer_shown = "";

  private title_layers: UITextElement[] = [];
  private info_img: UIImage | null = null;
  private info_top_text: UITextElement | null = null;
  private info_bottom_text: UITextElement | null = null;
  private timer_pill: RoundedRectangle | null = null;
  private close_button: UIImage | null = null;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: any,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    this.createTitle();

    if ( params.rewarded )
    {
      this.createCards();
      this.createBottomText();
      this.createClaimButton();
    } else {
      this.createInfoContent();
      this.createCloseButton();
      this.playInfoAppear();
    }
  }

  private createInfoContent(): void
  {
    const img_w = LayoutScaler.widthFromDesign( 795 );
    const img_h = LayoutScaler.heightFromDesign( 787 );
    const img_x = this.rect.getW() / 2 - img_w / 2;
    const img_y = this.rect.getH() / 2 - img_h / 2 - LayoutScaler.yFromDesign( 80 );

    const img = new UIImage(
      this.ctx,
      img_x,
      img_y,
      img_w,
      img_h,
      ImgID.UI_DailyCardInfoImg,
    );
    this.addChild( img );
    this.info_img = img;

    const top_text_h = LayoutScaler.heightFromDesign( 95 );
    const top_text_y = img_y + LayoutScaler.yFromDesign( 23 );
    const top_text_x = LayoutScaler.xFromDesign( 20 );

    const top_text = new UITextElement(
      this.ctx,
      top_text_x,
      top_text_y,
      this.rect.getW(),
      top_text_h,
      {
        localization_key: "DAILY_GIFTS.RECEIVED",
        font_size: LayoutScaler.textSizeFromDesign( 80 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );
    this.addChild( top_text );
    this.info_top_text = top_text;

    const bottom_text_h = LayoutScaler.heightFromDesign( 60 );
    const bottom_text_y = img_y + img_h;

    const bottom_text = new UITextElement(
      this.ctx,
      0,
      bottom_text_y,
      this.rect.getW(),
      bottom_text_h,
      {
        localization_key: "DAILY_GIFTS.NEXT_IN",
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );
    this.addChild( bottom_text );
    this.info_bottom_text = bottom_text;

    const pill_w = LayoutScaler.widthFromDesign( 300 );
    const pill_h = LayoutScaler.heightFromDesign( 70 );
    const pill_x = this.rect.getW() / 2 - pill_w / 2;
    const pill_y = bottom_text_y + bottom_text_h + LayoutScaler.yFromDesign( 20 );

    const pill = new RoundedRectangle(
      this.ctx,
      pill_x,
      pill_y,
      pill_w,
      pill_h,
      LayoutScaler.cornerRadiusFromDesign( 35 ),
      ColorScheme.hearts_timer_bg,
      {},
      false,
      ColorScheme.daily_card_reward_timer_border,
      LayoutScaler.widthFromDesign( 6 ),
      undefined,
      // {
      //   color: ColorScheme.daily_card_reward_timer_shadow,
      //   blur: LayoutScaler.widthFromDesign( 10 ),
      // } // TODO: fix shadow problem in RoundedRectangle
    );

    pill.setClickable( false );
    this.addChild( pill );
    this.timer_pill = pill;

    const clock_size = LayoutScaler.heightFromDesign( 58 );

    const clock = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 8 ),
      ( pill_h - clock_size ) / 2,
      clock_size,
      clock_size,
      ImgID.UI_ClockFrame
    );

    clock.setClickable( false );
    pill.addChild( clock );

    const shown = this.timerText();

    const timer_text = new UITextElement(
      this.ctx,
      clock_size,
      0,
      pill_w - clock_size,
      pill_h,
      {
        text: shown,
        font_size: LayoutScaler.textSizeFromDesign( 40 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.hearts_text,
        auto_wrap: false,
      }
    );

    timer_text.setClickable( false );
    pill.addChild( timer_text );
    this.timer_text = timer_text;
    this.timer_shown = shown;
  }

  private timerText(): string
  {
    const next_ms = RewardWidgetsService.nextMs( RewardWidgetsService.dailyCards()?.next_available_at ?? null );
    return formatShortDuration( Math.max( 0, next_ms - Date.now() ) );
  }

  private createCloseButton(): void
  {
    const close_size = LayoutScaler.widthFromDesign( 140 );
    const close_x = this.rect.getW() / 2 - close_size / 2;
    const close_y = this.rect.getH() - close_size - LayoutScaler.yFromDesign( 190 );

    const close = new UIImage(
      this.ctx,
      close_x,
      close_y,
      close_size,
      close_size,
      ImgID.IconOverlayClose,
      {},
      true,
    );
    close.setOnClick( () => OverlayManager.closeOverlay() );
    this.addChild( close );
    this.close_button = close;
  }

  private playInfoAppear(): void
  {
    const S = DailyCardRewardOverlay;
    const start = performance.now() + S.APPEAR_DELAY_MS;

    for ( const layer of this.title_layers )
      OverlayAppear.pop( layer, start );

    OverlayAppear.pop( this.info_img, start + 70, { from_scale: S.INFO_IMG_FROM_SCALE, overshoot: S.INFO_IMG_OVERSHOOT } );
    OverlayAppear.pop( this.info_top_text, start + 190 );
    OverlayAppear.rise( this.info_bottom_text, start + 250 );
    OverlayAppear.rise( this.timer_pill, start + 300 );
    OverlayAppear.pop( this.close_button, start + 380, { from_scale: S.CLOSE_FROM_SCALE } );
  }

  private createTitle(): void
  {
    const title_h = LayoutScaler.heightFromDesign( 200 );
    const title_y = LayoutScaler.yFromDesign( 140 );

    const title = new UITextElement(
      this.ctx,
      0,
      title_y,
      this.rect.getW(),
      title_h,
      {
        localization_key: "DAILY_GIFTS.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( 110 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.daily_card_title_1,
        outline_offset_y: LayoutScaler.yFromDesign( 3 ),
        line_height: 1.25,
      },
    );

    const title_1 = new UITextElement(
      this.ctx,
      0,
      title_y + LayoutScaler.yFromDesign( 3 ),
      this.rect.getW(),
      title_h,
      {
        localization_key: "DAILY_GIFTS.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( 110 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 22 ),
        outline_color: ColorScheme.daily_card_title_2,
        line_height: 1.25,
      },
    );

    const title_2 = new UITextElement(
      this.ctx,
      0,
      title_y + LayoutScaler.widthFromDesign( 5 ),
      this.rect.getW(),
      title_h,
      {
        localization_key: "DAILY_GIFTS.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( 75 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 30 ),
        outline_color: ColorScheme.daily_card_title_3,
        line_height: 1.35,
      },
    );

    this.addChild( title_2 );
    this.addChild( title_1 );
    this.addChild( title );
    this.title_layers = [ title_2, title_1, title ];
  }

  private createBottomText(): void
  {
    const text_h = LayoutScaler.heightFromDesign( 60 );
    const text_y = this.rect.getH() - text_h - LayoutScaler.yFromDesign( 250 );

    const text = new UITextElement(
      this.ctx,
      0,
      text_y,
      this.rect.getW(),
      text_h,
      {
        localization_key: "DAILY_GIFTS.SUBTITLE",
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        font_weight: 700,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
      },
    );

    this.addChild( text );
    this.bottom_text = text;
  }

  private createClaimButton(): void
  {
    const button_w = LayoutScaler.widthFromDesign( 700 );
    const button_h = LayoutScaler.heightFromDesign( 280 );
    const button_x = ( this.rect.getW() - button_w ) / 2;
    const button_y = this.rect.getH() - button_h - LayoutScaler.yFromDesign( 80 );

    const button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_GreenFrameBtn,
      {},
      true
    );

    const text = new UITextElement(
      this.ctx,
      0,
      0,
      button_w,
      button_h,
      {
        localization_key: "CLAIM",
        font_size: LayoutScaler.textSizeFromDesign( 70 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        outline_color: ColorScheme.daily_card_button_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        ...TextStyleScheme.buttonShadow(),
      }
    );
    button.addChild( text, true );

    button.setOnClick( () => this.onClaimClicked() );
    button.setClickable( false );
    button.setOpacityImmediate( 0 );

    this.addChild( button );
    this.claim_button = button;
  }

  private showClaimButton(): void
  {
    const fade = DailyCardRewardOverlay.BUTTON_FADE_MS;
    this.bottom_text?.hideElement( fade );
    this.claim_button?.showElement( fade );
    this.claim_button?.setClickable( true );
  }

  private onClaimClicked(): void
  {
    if ( !this.claim_button ) return;
    this.claim_button.setClickable( false );

    const stats_before = StatisticsStorage.load();
    const sources = this.claimFlySources();

    RewardWidgetsService.applyClaimedRewards();
    this.claim_button.hideElement( DailyCardRewardOverlay.BUTTON_FADE_MS );

    RewardFlyToHud.playThenClose( this.ctx, this, sources, stats_before, () =>
    {
      if ( OverlayManager.isOverlayOpen( this.getId() ) )
        OverlayManager.closeOverlay();
    } );
  }

  private claimFlySources(): RewardFlySource[]
  {
    const entries = this.selected_card ? this.card_reward_icons.get( this.selected_card ) ?? [] : [];

    return entries.map( entry => RewardFlySources.fromElement( entry.reward, entry.icon ) );
  }

  private createCards(): void
  {
    const card_count = RewardWidgetsService.dailyCards()?.cards ?? 9;
    const card_in_row = 3;

    const card_width = LayoutScaler.widthFromDesign( 266 );
    const card_height = LayoutScaler.heightFromDesign( 366 );

    const card_row_gap = LayoutScaler.widthFromDesign( 28 );
    const card_column_gap = LayoutScaler.heightFromDesign( 31 );

    const row_count = Math.ceil( card_count / card_in_row );
    const grid_width = card_in_row * card_width + ( card_in_row - 1 ) * card_row_gap;
    const grid_height = row_count * card_height + ( row_count - 1 ) * card_column_gap;
    const start_x = ( this.rect.getW() - grid_width ) / 2;
    const start_y = ( this.rect.getH() - grid_height ) / 2;

    for ( let i = 0; i < card_count; i++ )
    {
      const col = i % card_in_row;
      const row = Math.floor( i / card_in_row );

      const x = start_x + col * ( card_width + card_row_gap );
      const y = start_y + row * ( card_height + card_column_gap );
      const card = new UIImage(
        this.ctx,
        x,
        y,
        card_width,
        card_height,
        ImgID.UI_DailyCardRewardCardFront,
        undefined,
        true
      );
      card.setOnClick( () => this.selectCard( card ) );
      this.addChild( card );
      this.cards.push( card );
      this.card_bounds.set( card, { x, y, w: card_width, h: card_height } );
    }
  }

  private async selectCard( card: UIImage ): Promise<void>
  {
    if ( this.selected_card || this.is_claiming )
      return;

    this.is_claiming = true;
    for ( const other of this.cards )
      other.setClickable( false );

    try
    {
      const result = await RewardWidgetsService.claimDailyCard( this.cards.indexOf( card ) );
      this.card_rewards = result.cards;
    }
    catch ( e )
    {
      console.warn( "[DailyCards] Claim failed", e );
      this.is_claiming = false;
      const is_gone = e instanceof ApiError && e.status === 409;
      if ( is_gone )
      {
        OverlayManager.closeOverlay();
        return;
      }
      for ( const other of this.cards )
        other.setClickable( true );
      return;
    }

    this.selected_card = card;
    this.flipAll( card );
  }

  private flipAll( card: UIImage ): void
  {
    this.flip(
      card,
      () =>
      {
        card.setImageId( ImgID.UI_DailyCardRewardCardBack );
        this.addCardContent( card, this.rewardsFor( card ) );
        this.addGlowUnder( card );
      },
      () =>
      {
        this.onCardRevealed( card );
        this.flipOthers( card );
      }
    );
  }

  private flipOthers( pick: UIImage ): void
  {
    for ( const other of this.cards )
    {
      if ( other === pick )
        continue;

      this.flip(
        other,
        () =>
        {
          other.setImageId( ImgID.UI_DailyCardRewardCardBack );
          this.addCardContent( other, this.rewardsFor( other ) );
          this.dimCard( other );
        },
        () => {}
      );
    }
  }

  private flip( element: UIElement, on_edge: () => void, on_done: () => void ): void
  {
    const half = DailyCardRewardOverlay.FLIP_HALF_MS;

    const close = new TransformScale(
      element,
      true,
      new Vector2( 1, 1 ),
      new Vector2( 0, 1 ),
      performance.now(),
      half,
      { easing: Easing.EaseInQuad }
    );

    close.setOnFinished( () =>
    {
      on_edge();

      const open = new TransformScale(
        element,
        true,
        new Vector2( 0, 1 ),
        new Vector2( 1, 1 ),
        performance.now(),
        half,
        { easing: Easing.EaseOutQuad }
      );
      open.setOnFinished( () => on_done() );
      element.addTransform( open );
    } );

    element.addTransform( close );
  }

  private onCardRevealed( card: UIImage ): void
  {
    void card;
    this.showClaimButton();
  }

  private rewardsFor( card: UIImage ): DailyTaskRewardDto[]
  {
    return this.card_rewards[this.cards.indexOf( card )] ?? [];
  }

  private addCardContent( card: UIImage, rewards: DailyTaskRewardDto[] ): void
  {
    const bounds = this.card_bounds.get( card );
    if ( !bounds || rewards.length === 0 )
      return;

    const S = DailyCardRewardOverlay;
    const icon_area_h = LayoutScaler.heightFromDesign( S.REWARD_COIN_H );
    const amount_h = LayoutScaler.heightFromDesign( S.REWARD_AMOUNT_H );
    const overlap = LayoutScaler.heightFromDesign( S.REWARD_AMOUNT_OVERLAP );
    const gap = LayoutScaler.heightFromDesign( S.REWARD_ITEM_GAP );

    const item_h = icon_area_h + amount_h - overlap;
    const total_h = rewards.length * item_h + ( rewards.length - 1 ) * gap;
    let y = ( bounds.h - total_h ) / 2;
    const placed: { reward: DailyTaskRewardDto; icon: UIImage }[] = [];
    this.card_reward_icons.set( card, placed );

    for ( const reward of rewards )
    {
      const is_coin = reward.type === "coin";
      const icon_w = LayoutScaler.widthFromDesign( is_coin ? S.REWARD_COIN_W : S.REWARD_ICON_SIZE );
      const icon_h = LayoutScaler.heightFromDesign( is_coin ? S.REWARD_COIN_H : S.REWARD_ICON_SIZE );

      const icon = new UIImage(
        this.ctx,
        ( bounds.w - icon_w ) / 2,
        y + ( icon_area_h - icon_h ) / 2,
        icon_w,
        icon_h,
        is_coin ? ImgID.UI_ShopBlockImg : dailyTaskRewardIcon( reward.type )
      );
      icon.setClickable( false );

      const amount = new UITextElement(
        this.ctx,
        0,
        y + icon_area_h - overlap,
        bounds.w,
        amount_h,
        {
          text: isTimedRewardType( reward.type ) ? rewardAmountText( reward.type, reward.amount ) : `${reward.amount}`,
          font_size: LayoutScaler.textSizeFromDesign( 34 ),
          font_weight: 900,
          text_style: TextStyleScheme.rubik,
          text_color: ColorScheme.white,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
          outline_color: ColorScheme.daily_card_reward_outline,
          auto_wrap: false,
          scale_to_width: true,
        }
      );
      amount.setClickable( false );

      card.addChild( icon );
      card.addChild( amount );
      placed.push( { reward, icon } );
      y += item_h + gap;
    }
  }

  private dimCard( card: UIImage ): void
  {
    const bounds = this.card_bounds.get( card );
    if ( !bounds )
      return;

    const veil = new UICanvasRect(
      this.ctx,
      0,
      0,
      bounds.w,
      bounds.h,
      DailyCardRewardOverlay.DIM_COLOR,
      LayoutScaler.widthFromDesign( DailyCardRewardOverlay.CARD_CORNER_RADIUS )
    );
    veil.setClickable( false );
    veil.setOpacityImmediate( 0 );

    card.addChild( veil );
    veil.showElement( DailyCardRewardOverlay.GLOW_FADE_MS );
  }

  private addGlowUnder( card: UIImage ): void
  {
    const bounds = this.card_bounds.get( card );
    if ( !bounds || this.selected_glow )
      return;

    const inset = LayoutScaler.widthFromDesign( 4 );
    const glow = new UIRoundedGlow(
      this.ctx,
      bounds.x + inset,
      bounds.y + inset,
      bounds.w - inset * 2,
      bounds.h - inset * 2,
      DailyCardRewardOverlay.GLOW_COLOR,
      LayoutScaler.widthFromDesign( 40 ),
      LayoutScaler.widthFromDesign( 110 )
    );

    this.addChildAtIndex( glow, this.childIndexOf( this.cards[0] ) );

    glow.setOpacityImmediate( 0 );
    glow.showElement( DailyCardRewardOverlay.GLOW_FADE_MS );
    this.selected_glow = glow;
  }

  public getSelectedCard(): UIImage | null
  {
    return this.selected_card;
  }

  public update( now: number ): void
  {
    super.update( now );
    this.updateTimer();
  }

  private updateTimer(): void
  {
    if ( !this.timer_text )
      return;

    const text = this.timerText();
    if ( text === this.timer_shown )
      return;

    this.timer_shown = text;
    this.timer_text.setText( text );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "DailyCardRewardOverlay";
  }
}
